import { useEffect, useState } from "react";
import {
  AppAvatar,
  AppBorderBox,
  AppBox,
  AppButton,
  AppModal,
  AppPaper,
  AppSkeletonGlobalCardList,
  AppText,
  AppTitle,
} from "../../../../../commons/components";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { FootballTeamManager } from "../../../../../commons/models/football/football-team-manager";
import {
  fetchManageTeamManagerList,
  IManageTeamManagersOption,
} from "../../../../../redux/slices/football/admin/team/manager/manage-team-managers-slice";
import UserSearch from "../../../users/components/UserSearch";
import TeamManagerRemove from "./TeamManagerRemove";
import { IFootballTeamManager } from "../../../../../commons/models/football/interface/i-football-team-manager";
import IconPersonDelete from "../../../../../commons/components/icons/person-delete";
import TeamManagerRemovalInprogress from "./TeamManagerRemoveInprogress";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import { teamManagerRemove } from "../../../../../redux/slices/football/admin/team/manager/manage-team-manager-remove-slice";
import { Can } from "../../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import IconPersonAdd from "../../../../../commons/components/icons/person-add";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import setDocumentTitle from "../../../../../commons/utilities/document-title";
import { User } from "../../../../../commons/models/user";
import TeamManagerCard from "./TeamManagerCard";
import { teamManagerAdd } from "../../../../../redux/slices/football/admin/team/manager/manage-team-manager-add-slice";
import TeamManagerAddConfirm from "./TeamManagerAddConfirmation";

export default function TeamManager() {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { teamInfo } = useAppSelector((state) => state.footballManageTeam);
  const [isManagerAdd, setManagerAdd] = useState(false);
  const [selectedTeamManager, setSelectedTeamManager] = useState<FootballTeamManager | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { manageTeamManagersList, manageTeamManagersListStatus } = useAppSelector(
    (state) => state.footballManageTeamManagers
  );
  const { manageTeamManagerRemoveStatus } = useAppSelector((state) => state.footballManageTeamManagerRemove);
  const { manageTeamManagerAddStatus } = useAppSelector((state) => state.footballManageTeamManagerAdd);
  const [isRemoveTeamManager, setRemoveTeamManager] = useState(false);

  useEffect(() => {
    if (teamInfo.id) {
      const managerListOption: IManageTeamManagersOption = {
        teamId: teamInfo.id,
      };
      dispatch(fetchManageTeamManagerList(managerListOption));
    }
  }, [teamInfo]);

  const handleManagerAdd = () => {
    if (selectedUser) {
      dispatch(
        teamManagerAdd({
          teamId: teamInfo.id,
          userId: selectedUser.id,
        })
      )
        .unwrap()
        .then(() => {
          setSelectedTeamManager(null);
          addSnackbar({
            key: "manager-add",
            text: "Manager Added Successfully",
            variant: "success",
          });
          dispatch(
            fetchManageTeamManagerList({
              teamId: teamInfo.id,
            })
          );
          setSelectedTeamManager(null);
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
    setSelectedUser(null);
  };

  const handleManagerRemoveConfirm = () => {
    if (selectedTeamManager) {
      dispatch(
        teamManagerRemove({
          teamId: teamInfo.id,
          managerId: selectedTeamManager.id,
        })
      )
        .unwrap()
        .then(() => {
          setSelectedTeamManager(null);
          addSnackbar({
            key: "manager-remove",
            text: "Manager Removed Successfully",
            variant: "success",
          });
          dispatch(
            fetchManageTeamManagerList({
              teamId: teamInfo.id,
            })
          );
          setSelectedTeamManager(null);
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
  };

  return (
    <>
      <AppBox gap="sm" flexDirection="column">
        <AppBox alignItems="center" justifyContent="space-between">
          <AppTitle as="h5">Managers</AppTitle>
          <Can I={RolePolicyActionEnum.CREATE} this={teamInfo}>
            {manageTeamManagersList.length > 0 && (
              <AppButton size="sm" label="Add Manager" variant="light" onClick={() => setManagerAdd(true)} />
            )}
          </Can>
        </AppBox>
        {manageTeamManagersList.length === 0 ? (
          <AppPaper padding="sm" shadow="xs">
            <AppBorderBox borderVariant="dashed">
              <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center" p="sm">
                <AppBox alignItems="center" justifyContent="center" gap="xs">
                  <Can I={RolePolicyActionEnum.CREATE} this={teamInfo}>
                    <AppBox>
                      <IconPersonAdd width={40} height={40} color="rgba(var(--border-200))" />
                    </AppBox>
                  </Can>
                  <AppText fontWeight="semibold">There are not any managers in the team.</AppText>
                </AppBox>
                <Can I={RolePolicyActionEnum.CREATE} this={teamInfo}>
                  <AppButton size="sm" label="Add Manager" onClick={() => setManagerAdd(true)} />
                </Can>
              </AppBox>
            </AppBorderBox>
          </AppPaper>
        ) : (
          <>
            {manageTeamManagersListStatus === StatusEnum.Loading ? (
              <>
                <AppSkeletonGlobalCardList numberOfItems={2} withAvatar noTitle />
              </>
            ) : (
              <AppPaper padding="none" shadow="xs" style={{ maxHeight: "20.5rem" }} className="overflow-y-auto">
                <AppBox flexDirection="column">
                  {manageTeamManagersList.all().map((manager, index) => (
                    <AppBorderBox
                      key={index}
                      className="w-100"
                      border={
                        manageTeamManagersList.length === 1
                          ? []
                          : index === 0
                          ? ["Bottom"]
                          : index === manageTeamManagersList.length - 1
                          ? ["Top"]
                          : ["Top", "Bottom"]
                      }
                    >
                      <TeamManagerCard
                        managerInfo={manager}
                        onPlayerRemove={() => {
                          setSelectedTeamManager(manager);
                          setRemoveTeamManager(true);
                        }}
                      />
                    </AppBorderBox>
                  ))}
                </AppBox>
              </AppPaper>
            )}
          </>
        )}
      </AppBox>
      <AppModal
        opened={isManagerAdd}
        onClose={() => {
          setManagerAdd(false);
        }}
        titleIcon={<IconPersonAdd />}
        title={"Add Manager To Team"}
        withoutButtons
      >
        <UserSearch
          userSelectedUsers={manageTeamManagersList.length > 0 ? (manageTeamManagersList.all() as User[]) : []}
          onSelectedUsers={(users) => {
            setManagerAdd(false);
            setSelectedUser(users[0]);
          }}
        />
      </AppModal>
      {selectedUser && (
        <AppModal
          opened={selectedUser !== null}
          onClose={() => {
            setSelectedUser(null);
          }}
          titleIcon={<IconPersonAdd />}
          title={"Add Manager To Team"}
          onSave={() => {
            handleManagerAdd();
          }}
          withLoadingButton={manageTeamManagerAddStatus === StatusEnum.Loading}
        >
          <TeamManagerAddConfirm user={selectedUser} />
        </AppModal>
      )}
      <AppModal
        opened={isRemoveTeamManager}
        onClose={() => {
          setRemoveTeamManager(false);
          setSelectedTeamManager(null);
        }}
        title="Want To Remove This Manager?"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        confirmButtonLabel="Remove"
        onSave={() => {
          setRemoveTeamManager(false);
          handleManagerRemoveConfirm();
        }}
      >
        <TeamManagerRemove teamManager={selectedTeamManager as IFootballTeamManager} />
      </AppModal>
      <AppModal
        opened={manageTeamManagerRemoveStatus === StatusEnum.Loading}
        title="Removing Team"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        withoutCancelButton
        withoutButtons
        withoutCloseButton
      >
        <TeamManagerRemovalInprogress teamManager={selectedTeamManager as IFootballTeamManager} />
      </AppModal>
    </>
  );
}
